import { TodoTree, TodoItem } from '../types/todo';
import { getItemById, getItemPath } from './tree';
import { TaskScoringConfig, DEFAULT_TASK_SCORING_CONFIG, MAX_TOP_TASKS } from '../config/taskScoring';

export interface ScoredTask {
  id: string;
  item: TodoItem;
  score: number;
  path: string[]; // Array of task IDs from root to this task
  pathNames: string[]; // Array of task names for display
}

/**
 * Finds all leaf nodes (tasks with no children) that are not blocked, done, or archived
 */
function findEligibleLeafNodes(tree: TodoTree): Array<{ id: string; item: TodoItem }> {
  const leafNodes: Array<{ id: string; item: TodoItem }> = [];

  for (const [id, item] of Object.entries(tree)) {
    // Must be a leaf node (no children)
    if (item.children.length === 0) {
      // Must not be blocked, done, or archived
      if (!item.blocked && !item.done && !item.archived) {
        leafNodes.push({ id, item });
      }
    }
  }

  return leafNodes;
}

/**
 * Calculates the score for a specific path from root to leaf
 */
function calculatePathScore(
  tree: TodoTree,
  path: string[],
  config: TaskScoringConfig = DEFAULT_TASK_SCORING_CONFIG
): number {
  let score = config.baseScore;

  for (const nodeId of path) {
    const item = getItemById(tree, nodeId);
    if (!item) {
      return 0; // Invalid path
    }

    // Apply status multipliers
    if (item.blocked) {
      score *= config.status.blocked;
    } else if (item.archived) {
      score *= config.status.archived;
    } else if (item.done) {
      score *= config.status.done;
    }

    // Apply priority multipliers
    score *= config.priority[item.priority];

    // If score becomes 0, no point in continuing
    if (score === 0) {
      break;
    }
  }

  return score;
}

/**
 * Gets the names of all items in a path for display purposes
 */
function getPathNames(tree: TodoTree, path: string[]): string[] {
  return path
    .map(id => getItemById(tree, id)?.name)
    .filter((name): name is string => name !== undefined);
}

/**
 * Calculates and returns the top scoring unblocked leaf tasks
 */
export function getTopTasks(
  tree: TodoTree,
  config: TaskScoringConfig = DEFAULT_TASK_SCORING_CONFIG
): ScoredTask[] {
  // Find all eligible leaf nodes
  const leafNodes = findEligibleLeafNodes(tree);

  // Calculate scores for each leaf node
  const scoredTasks: ScoredTask[] = [];

  for (const { id, item } of leafNodes) {
    const path = getItemPath(tree, id);
    if (!path) {
      continue; // Skip if we can't find the path
    }

    const score = calculatePathScore(tree, path, config);

    // Only include tasks with positive scores
    if (score > 0) {
      const pathNames = getPathNames(tree, path);

      scoredTasks.push({
        id,
        item,
        score,
        path,
        pathNames,
      });
    }
  }

  // Sort by score (highest first) and limit to MAX_TOP_TASKS
  return scoredTasks
    .sort((a, b) => b.score - a.score)
    .slice(0, Math.min(MAX_TOP_TASKS, scoredTasks.length));
}

/**
 * Gets the count of unblocked leaf tasks for display purposes
 */
export function getUnblockedTaskCount(tree: TodoTree): number {
  return findEligibleLeafNodes(tree).length;
}